"use client";
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import styles from './Navbar.module.css';
import { buildNavFromPaths } from './props_utils';
import { NAVBAR_FILE_LIST } from '@/generated/navbarPaths';

interface NavItem {
  directory: string;
  files: string[];
  subdirectories: NavItem[];
}

/* Built once at module load. buildNavFromPaths is a pure synchronous transform
   over a build-time generated list, so this is identical on server and client
   and is safe to use as the initial render.

   It MUST NOT go back into a useEffect. The nav used to be populated only
   after mount, which meant the server shipped "Loading navigation..." and just
   hydration swapped in the real thing. Any browser that fails to hydrate is
   then stuck on that placeholder permanently — which is exactly what Safari
   15.6 does here, since Next 16's own main.js uses class static blocks
   (Safari 16.4+) and dies with a SyntaxError before React ever boots.
   Server-rendering the nav also means it works with JS disabled. */
const STATIC_NAV_ITEMS: NavItem[] = buildNavFromPaths(
  Array.from(NAVBAR_FILE_LIST || []) as string[]
) as NavItem[];

interface NavbarProps {
  initialNavItems?: NavItem[];
  onHamburgerToggle?: (isHamburger: boolean) => void;
}

const Navbar: React.FC<NavbarProps> = ({ initialNavItems = [], onHamburgerToggle }) => {
  const pathname = usePathname();
  const router = useRouter();
  const [navItems] = useState<NavItem[]>(
    initialNavItems.length ? initialNavItems : STATIC_NAV_ITEMS
  );
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [isHamburger, setIsHamburger] = useState(false);
  const [isHamburgerOpen, setIsHamburgerOpen] = useState(false);
  // Hover-capable (mouse) devices navigate on a single click — the submenu is
  // already shown on hover. Touch devices get the two-tap behaviour.
  const [canHover, setCanHover] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);
  const hamburgerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    setCanHover(window.matchMedia('(hover: hover) and (pointer: fine)').matches);
  }, []);
  // Collapse the mobile hamburger (and any open dropdown) on navigation — fires
  // on link clicks, subdirectory selection, and back/forward.
  useEffect(() => {
    setIsHamburgerOpen(false);
    setOpenDropdown(null);
  }, [pathname]);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        (navRef.current && !navRef.current.contains(event.target as Node)) &&
        (hamburgerRef.current && !hamburgerRef.current.contains(event.target as Node))
      ) {
        setOpenDropdown(null);
        setIsHamburgerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (navRef.current) navRef.current.classList.toggle(styles.scrolled, window.scrollY > 50);
      if (hamburgerRef.current) hamburgerRef.current.classList.toggle(styles.scrolled, window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    let debounceTimer: NodeJS.Timeout;

    const isMobileView = () =>
      typeof window !== 'undefined' &&
      (window.matchMedia
        ? window.matchMedia('(max-width: 767px)').matches
        : window.innerWidth < 768);

    const updateLayout = () => {
      const isMobile = isMobileView();

      // When the hamburger is mounted the inline bar isn't in the DOM, so we
      // can't measure rows. Decide on width alone — once the inline bar mounts
      // it re-measures on the next layout pass and can fall back if too tall.
      if (!navRef.current) {
        setIsHamburger(isMobile);
        if (onHamburgerToggle) onHamburgerToggle(isMobile);
        return;
      }

      const inner = navRef.current.querySelector(`.${styles.navBarInner}`);
      if (!inner) return;

      // Count how many rows the TOP-LEVEL dropdowns wrap into. Only the direct
      // children of navBarInner are top-level; nested dropdowns live inside
      // (hidden) panels and must not be counted.
      const tops = Array.from(inner.children).filter((el) =>
        (el as HTMLElement).classList.contains(styles.dropdown),
      );

      const tolerance = 5;
      const uniqueRowPositions: number[] = [];
      tops.forEach((el) => {
        const top = el.getBoundingClientRect().top;
        if (!uniqueRowPositions.some((p) => Math.abs(p - top) < tolerance)) {
          uniqueRowPositions.push(top);
        }
      });
      const uniqueRows = uniqueRowPositions.length;

      // Hamburger on mobile only. On desktop/large screens the bar stays inline
      // and is allowed to wrap into multiple rows of dropdowns (multiple per
      // row). A high safety cap still falls back to the hamburger if the bar
      // would become absurdly tall.
      const shouldBeHamburger = isMobile || uniqueRows > 6;
      setIsHamburger(shouldBeHamburger);
      if (onHamburgerToggle) onHamburgerToggle(shouldBeHamburger);
      if (!shouldBeHamburger) {
        setIsHamburgerOpen(false);
        setOpenDropdown(null);
      }
    };

    const debouncedUpdateLayout = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(updateLayout, 100);
    };

    updateLayout();
    window.addEventListener('resize', debouncedUpdateLayout);
    window.addEventListener('orientationchange', debouncedUpdateLayout);
    // React to the mobile breakpoint flipping (more reliable than resize alone
    // on some mobile browsers, e.g. when the URL bar shows/hides).
    const mql = typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(max-width: 767px)')
      : null;
    mql?.addEventListener('change', debouncedUpdateLayout);
    return () => {
      window.removeEventListener('resize', debouncedUpdateLayout);
      window.removeEventListener('orientationchange', debouncedUpdateLayout);
      mql?.removeEventListener('change', debouncedUpdateLayout);
      clearTimeout(debounceTimer);
    };
  }, [navItems, onHamburgerToggle]);

  const isActive = (path: string) => {
    if (!pathname) {
      return false
    }
    const normalizedPathname = pathname.replace(/\/$/, '');
    const normalizedPath = path.replace(/\/$/, '');
    return normalizedPathname === normalizedPath || normalizedPathname.startsWith(normalizedPath + '/');
  };

  const toggleDropdown = (path: string) => {
    setOpenDropdown(openDropdown === path ? null : path);
  };

  // Category tap behaviour:
  //  • inline desktop nav → click navigates (submenu shows on hover)
  //  • hamburger (any device) or touch → two-tap: first tap opens the submenu
  //    (accordion), second tap navigates to the category home
  const handleCategoryClick = (
    e: React.MouseEvent,
    dropdownId: string,
    currentPath: string,
  ) => {
    e.preventDefault();
    // Only the inline (non-hamburger) desktop nav navigates on a single click,
    // because there the submenu is reachable by hover. In the hamburger panel
    // hover isn't used, so a click toggles the submenu instead.
    const navigateOnClick = canHover && !isHamburger;
    if (navigateOnClick || openDropdown === dropdownId) {
      setOpenDropdown(null);
      setIsHamburgerOpen(false);
      router.push(`/${currentPath}`);
    } else {
      setOpenDropdown(dropdownId);
    }
  };

  const formatName = (name: string) =>
    name.split('-').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

  // Recursive function to render nav items
  const renderNavItems = (items: NavItem[], parentPath: string = '') => {
    return items.map(({ directory, files, subdirectories }) => {
      const currentPath = parentPath ? `${parentPath}/${directory}` : directory;
      const dropdownId = `${currentPath}-${directory}`;
      const hasDropdown = files.length > 0 || subdirectories.length > 0;

      // Only wrap in Link if there’s no dropdown (i.e., it’s a leaf node with index.tsx)
      const ButtonComponent = hasDropdown ? (
        <button
          className={`${styles.dropBtn} ${isActive(currentPath) ? styles.active : ''}`}
          aria-haspopup="true"
          aria-expanded={openDropdown === dropdownId}
          onClick={(e) => handleCategoryClick(e, dropdownId, currentPath)}
        >
          {formatName(directory)}
          <span className={styles.caret} aria-hidden="true">▾</span>
        </button>
      ) : (
        <Link href={`/${currentPath}`}>
          <button
            className={`${styles.dropBtn} ${isActive(currentPath) ? styles.active : ''}`}
          >
            {formatName(directory)}
          </button>
        </Link>
      );

      return (
        <div key={dropdownId} className={styles.dropdown}>
          {ButtonComponent}
          {hasDropdown && (
            <div
              className={`${styles.dropdownContent} ${openDropdown === dropdownId ? styles.open : ''}`}
            >
              {files.map((file) => (
                <Link
                  key={`${currentPath}/${file}`}
                  href={`/${currentPath}/${file}`}
                  className={`${styles.dropdownItem} ${isActive(`${currentPath}/${file}`) ? styles.active : ''}`}
                  onClick={() => { setOpenDropdown(null); setIsHamburgerOpen(false); }}
                >
                  {formatName(file)}
                </Link>
              ))}
              {subdirectories.length > 0 && renderNavItems(subdirectories, currentPath)}
            </div>
          )}
        </div>
      );
    });
  };

  const hamburgerMenu = (
    <div className={styles.hamburgerContainer} ref={hamburgerRef}>
      <button
        className={styles.hamburgerButton}
        onClick={() => setIsHamburgerOpen(!isHamburgerOpen)}
      >
        ☰
      </button>
      {isHamburgerOpen && (
        <nav className={styles.hamburgerMenu}>
          <div className={styles.navBarInner}>
            {navItems.length === 0 ? (
              <div>Loading navigation...</div>
            ) : (
              (<>{renderNavItems(navItems)}<div className={styles.dropdown}><a className={styles.dropBtn} href="/videos/">Videos</a></div><div className={styles.dropdown}><a className={styles.dropBtn} href="/pdfs/">PDFs</a></div></>)
            )}
          </div>
        </nav>
      )}
    </div>
  );

  const normalNavbar = (
    <nav className={styles.navContainer} ref={navRef}>
      <div className={styles.navBar}>
        <div className={styles.navBarInner}>
          {navItems.length === 0 ? (
            <div>Loading navigation...</div>
          ) : (
            (<>{renderNavItems(navItems)}<div className={styles.dropdown}><a className={styles.dropBtn} href="/videos/">Videos</a></div><div className={styles.dropdown}><a className={styles.dropBtn} href="/pdfs/">PDFs</a></div></>)
          )}
        </div>
      </div>
    </nav>
  );

  return <>{isHamburger ? hamburgerMenu : normalNavbar}</>;
};

export default Navbar;
